import { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { startVersionPoller, reloadToLatest } from '@/lib/build-version';
import { useAppUpdateStore } from '@/stores/app-update-store';

/**
 * A single bottom-right "new version available" card. Used both for the host
 * shell's own deploy (UpdateAvailableBanner) and for each embedded app that
 * announced a newer build via `rumio:update-available` (contract v1.13).
 */
function UpdateCard({
  title,
  onReload,
  onDismiss,
}: {
  title: string;
  /** Returns a promise so the button can show a reloading state until the
   *  page/iframe actually navigates away. */
  onReload: () => void | Promise<void>;
  onDismiss: () => void;
}) {
  const [reloading, setReloading] = useState(false);

  return (
    <div
      role="status"
      aria-live="polite"
      className="relative flex items-start gap-3 rounded-lg border border-line bg-surface p-4 shadow-lg max-w-sm"
    >
      <RefreshCw className="mt-0.5 h-5 w-5 shrink-0 text-sage-700" aria-hidden="true" />
      <div className="flex-1">
        <p className="text-sm font-medium text-ink">{title}</p>
        <p className="mt-0.5 text-xs text-ink-soft">
          Reload to get the latest features and fixes.
        </p>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            disabled={reloading}
            onClick={async () => {
              setReloading(true);
              await onReload();
            }}
            className="rounded-md bg-cta px-3 py-1.5 text-xs font-semibold text-cta-fg hover:bg-cta-deep disabled:opacity-60"
          >
            {reloading ? 'Reloading…' : 'Reload now'}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-md border border-line-strong px-3 py-1.5 text-xs font-medium text-ink-soft hover:bg-surface-soft"
          >
            Later
          </button>
        </div>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        className="-m-1 rounded p-1 text-ink-muted hover:text-ink-soft"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function UpdateAvailableBanner() {
  // Host shell deploy-freshness (polls /version.txt against the build-version
  // meta baseline).
  const [showShell, setShowShell] = useState(false);

  // Embedded apps that reported a newer build of themselves (contract v1.13).
  const appUpdates = useAppUpdateStore((s) => s.updates);
  const clearUpdate = useAppUpdateStore((s) => s.clearUpdate);

  useEffect(() => {
    const stop = startVersionPoller(() => setShowShell(true));
    return stop;
  }, []);

  const appEntries = Object.values(appUpdates);
  if (!showShell && appEntries.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] flex flex-col items-end gap-3">
      {appEntries.map((entry) => (
        <UpdateCard
          key={entry.appId}
          title={`A new version of ${entry.name} is available`}
          onReload={() => {
            entry.reload();
            clearUpdate(entry.appId);
          }}
          onDismiss={() => clearUpdate(entry.appId)}
        />
      ))}
      {showShell && (
        <UpdateCard
          title="A new version is available"
          onReload={() => reloadToLatest()}
          onDismiss={() => setShowShell(false)}
        />
      )}
    </div>
  );
}
